/**
 * Карта v3 — геометрия и вьюпорт.
 *
 * Здесь нет ни рендера, ни состояния — только чистые функции,
 * чтобы их можно было проверять по отдельности.
 */
import { GRID } from './const';

// ---------- вьюпорт ----------
// Одна трансформация на всю сцену: screen = world * scale + pan.
// Вложенность локаций логическая (parentId), а не координатная —
// все координаты в модели абсолютные мировые. Это снимает целый класс
// ошибок, на которых спотыкалась карта v1 с относительными позициями.

export const worldToScreen = (vp, x, y) => ({
  x: x * vp.scale + vp.x,
  y: y * vp.scale + vp.y,
});

export const screenToWorld = (vp, x, y) => ({
  x: (x - vp.x) / vp.scale,
  y: (y - vp.y) / vp.scale,
});

export function zoomAt(vp, screenX, screenY, factor, min, max) {
  const next = clamp(vp.scale * factor, min, max);
  const k = next / vp.scale;
  return {
    scale: next,
    x: screenX - (screenX - vp.x) * k,
    y: screenY - (screenY - vp.y) * k,
  };
}

export function fitTo(box, width, height, pad = 60) {
  if (!box || !isFinite(box.w) || box.w <= 0 || box.h <= 0) {
    return { scale: 1, x: width / 2, y: height / 2 };
  }
  const scale = Math.min((width - pad * 2) / box.w, (height - pad * 2) / box.h, 2);
  return {
    scale,
    x: width / 2 - (box.x + box.w / 2) * scale,
    y: height / 2 - (box.y + box.h / 2) * scale,
  };
}

// ---------- скаляры ----------
export const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
export const snap = (v, step = GRID) => Math.round(v / step) * step;
export const lerp = (a, b, t) => a + (b - a) * t;
export const near = (a, b, eps = 0.5) => Math.abs(a - b) <= eps;

// ---------- прямоугольники ----------
export const rectOf = (n) => ({ x: n.x, y: n.y, w: n.w, h: n.h });
export const cx = (r) => r.x + r.w / 2;
export const cy = (r) => r.y + r.h / 2;

export const inflate = (r, d) => ({ x: r.x - d, y: r.y - d, w: r.w + d * 2, h: r.h + d * 2 });

export const containsPt = (r, x, y) =>
  x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h;

export const rectsOverlap = (a, b) =>
  a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export const containsRect = (outer, inner) =>
  inner.x >= outer.x && inner.y >= outer.y &&
  inner.x + inner.w <= outer.x + outer.w &&
  inner.y + inner.h <= outer.y + outer.h;

export function unionRect(list) {
  if (!list || !list.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const r of list) {
    if (!r || !isFinite(r.x)) continue;
    if (r.x < x0) x0 = r.x;
    if (r.y < y0) y0 = r.y;
    if (r.x + r.w > x1) x1 = r.x + r.w;
    if (r.y + r.h > y1) y1 = r.y + r.h;
  }
  if (!isFinite(x0)) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/** Минимальный сдвиг, выталкивающий a из b (minimum translation vector). */
export function mtv(a, b) {
  const dx1 = b.x + b.w - a.x;      // толкать вправо
  const dx2 = a.x + a.w - b.x;      // толкать влево
  const dy1 = b.y + b.h - a.y;      // толкать вниз
  const dy2 = a.y + a.h - b.y;      // толкать вверх
  const mx = dx1 < dx2 ? dx1 : -dx2;
  const my = dy1 < dy2 ? dy1 : -dy2;
  return Math.abs(mx) < Math.abs(my) ? { x: mx, y: 0 } : { x: 0, y: my };
}

// ---------- отрезки ----------
export function distToSeg(px, py, x1, y1, x2, y2) {
  const dx = x2 - x1, dy = y2 - y1;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - x1, py - y1);
  let t = ((px - x1) * dx + (py - y1) * dy) / len2;
  t = clamp(t, 0, 1);
  return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
}

/** Ближайшая точка полилинии + индекс сегмента. Нужна и для попадания, и для подписей. */
export function nearestOnPolyline(pts, px, py) {
  let best = { d: Infinity, x: 0, y: 0, seg: 0, t: 0 };
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const x1 = pts[i], y1 = pts[i + 1], x2 = pts[i + 2], y2 = pts[i + 3];
    const dx = x2 - x1, dy = y2 - y1;
    const len2 = dx * dx + dy * dy;
    let t = len2 === 0 ? 0 : ((px - x1) * dx + (py - y1) * dy) / len2;
    t = clamp(t, 0, 1);
    const qx = x1 + t * dx, qy = y1 + t * dy;
    const d = Math.hypot(px - qx, py - qy);
    if (d < best.d) best = { d, x: qx, y: qy, seg: i / 2, t };
  }
  return best;
}

export function polylineLength(pts) {
  let L = 0;
  for (let i = 0; i + 3 < pts.length; i += 2) {
    L += Math.hypot(pts[i + 2] - pts[i], pts[i + 3] - pts[i + 1]);
  }
  return L;
}

/** Точка на полилинии по доле длины — место для подписи. */
export function pointAtFraction(pts, f) {
  const total = polylineLength(pts);
  if (total === 0) return { x: pts[0] || 0, y: pts[1] || 0, seg: 0 };
  let target = total * clamp(f, 0, 1);
  for (let i = 0; i + 3 < pts.length; i += 2) {
    const l = Math.hypot(pts[i + 2] - pts[i], pts[i + 3] - pts[i + 1]);
    if (target <= l || i + 5 >= pts.length) {
      const t = l === 0 ? 0 : target / l;
      return {
        x: lerp(pts[i], pts[i + 2], t),
        y: lerp(pts[i + 1], pts[i + 3], t),
        seg: i / 2,
      };
    }
    target -= l;
  }
  return { x: pts[pts.length - 2], y: pts[pts.length - 1], seg: 0 };
}

/** Пересечение двух ОСЕВЫХ отрезков (нужно для мостиков на перекрёстках). */
export function segCross(ax1, ay1, ax2, ay2, bx1, by1, bx2, by2) {
  const aH = near(ay1, ay2), bH = near(by1, by2);
  if (aH === bH) return null;             // параллельные — мостик не рисуем
  if (aH) {
    const x = bx1, y = ay1;
    if (x >= Math.min(ax1, ax2) && x <= Math.max(ax1, ax2) &&
        y >= Math.min(by1, by2) && y <= Math.max(by1, by2)) return { x, y };
    return null;
  }
  const x = ax1, y = by1;
  if (x >= Math.min(bx1, bx2) && x <= Math.max(bx1, bx2) &&
      y >= Math.min(ay1, ay2) && y <= Math.max(ay1, ay2)) return { x, y };
  return null;
}

/** Убирает лишние вершины: коллинеарные и нулевые сегменты. */
export function simplify(pts) {
  if (pts.length <= 4) return pts.slice();
  const out = [pts[0], pts[1]];
  for (let i = 2; i + 1 < pts.length; i += 2) {
    const x = pts[i], y = pts[i + 1];
    const px = out[out.length - 2], py = out[out.length - 1];
    if (near(px, x, 0.01) && near(py, y, 0.01)) continue;      // дубль
    if (out.length >= 4) {
      const qx = out[out.length - 4], qy = out[out.length - 3];
      const collinearH = near(qy, py, 0.01) && near(py, y, 0.01);
      const collinearV = near(qx, px, 0.01) && near(px, x, 0.01);
      if (collinearH || collinearV) { out[out.length - 2] = x; out[out.length - 1] = y; continue; }
    }
    out.push(x, y);
  }
  return out;
}

/** Натуральная сортировка имён интерфейсов: eth2 < eth10, Gi0/1 < Gi0/10. */
export function naturalCompare(a, b) {
  const re = /(\d+)|(\D+)/g;
  const ax = String(a || '').toLowerCase().match(re) || [];
  const bx = String(b || '').toLowerCase().match(re) || [];
  for (let i = 0; i < Math.min(ax.length, bx.length); i++) {
    const an = parseInt(ax[i], 10), bn = parseInt(bx[i], 10);
    if (!isNaN(an) && !isNaN(bn)) { if (an !== bn) return an - bn; }
    else if (ax[i] !== bx[i]) return ax[i] < bx[i] ? -1 : 1;
  }
  return ax.length - bx.length;
}
